import {
  Button,
  Chip,
  ConfirmButton,
  ErrorBanner,
  Picker,
  SkeletonRows,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import * as React from 'react'
import { describeError } from '../../lib/api/client'
import type { CloseoutEscalation, ReminderBatchOut, ReminderItemOut } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import {
  closeoutSettingsQuery,
  previewQuery,
  type ReminderMode,
  reasonText,
  recipientKind,
  remindersQuery,
  teamNotificationsQuery,
  useSaveCloseoutSettings,
  useSaveTeamNotifications,
} from './api'
import { AutoAnswerPanel } from './auto-answer-panel'
import { StaleClosePanel } from './stale-close-panel'

/**
 * The close-out reminders view (#121): the shadow-mode drafts Clayton reviews before flipping reminders to auto,
 * every recorded outcome, what the next run would do, and the manager switches (reminders and vendor chase
 * off/shadow/auto, escalation, Desk time nudges). Quiet hours are fixed by the scope and shown, not edited.
 *
 * Bodies are shown as text: contractor and vendor bodies are HTML built from objects by the engine, and they are
 * never injected into the page. The contractor portal link is inserted only at send time, so drafts never hold one.
 */

const KIND_LABEL = { person: 'Staff', contractor: 'Contractor', vendor: 'Vendor', customer: 'Customer', unknown: 'Recipient' } as const
const STATUS_TONE = { drafted: 'primary', sent: 'success', blocked: 'warning', failed: 'danger' } as const

/** The engine's HTML bodies as readable text (list items become bullets), without rendering any markup. */
export function bodyText(body: string): string {
  if (!/<[a-z][\s\S]*>/i.test(body)) return body
  const marked = body
    .replace(/<li[^>]*>/gi, '\n• ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
  const doc = new DOMParser().parseFromString(marked, 'text/html')
  return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim()
}

function when(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : formatWhen(d.toISOString(), { style: 'absolute' })
}

function Items({ items }: { items: ReminderItemOut[] }) {
  if (!items.length) return null
  return (
    <ul className="mt-1.5 space-y-0.5 text-xs">
      {items.map((it) => (
        <li key={`${it.key}-${it.section}`} className="flex items-center gap-1.5">
          <Link to="/case/$id" params={{ id: it.case_number }} className="font-medium text-primary">
            {it.case_number}
          </Link>
          <span className="text-muted-foreground">{it.label}</span>
          {it.section === 'escalation' ? (
            <Chip tone="danger" size="sm">
              escalated
            </Chip>
          ) : null}
          {it.section === 'billing' ? (
            <Chip tone="neutral" size="sm">
              ready to bill
            </Chip>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

function BatchCard({ b }: { b: ReminderBatchOut }) {
  const kind = recipientKind(b.recipient)
  return (
    <li className="rounded-lg border border-border bg-card p-3" data-testid="reminder-batch">
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip tone={STATUS_TONE[b.status]}>{b.status}</Chip>
        <Chip tone="outline">{KIND_LABEL[kind]}</Chip>
        <span className="font-medium">{b.recipient_display ?? 'Unknown recipient'}</span>
        <span className="ml-auto text-xs text-subtle">
          {when(b.at)}
          {b.channel ? ` · ${b.channel.replaceAll('_', ' ')}` : ''}
        </span>
      </div>
      {b.subject ? <p className="mt-1.5 text-sm font-medium">{b.subject}</p> : null}
      {b.body ? (
        <pre className="mt-1 max-h-52 overflow-y-auto whitespace-pre-wrap rounded-md bg-muted/60 p-2 font-sans text-xs leading-5">
          {b.body_text ?? bodyText(b.body)}
        </pre>
      ) : null}
      {b.reasons?.length ? (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {b.reasons.map((r) => (
            <Chip key={r} tone="warning" title={r}>
              {reasonText(r)}
            </Chip>
          ))}
        </div>
      ) : null}
      <Items items={b.items ?? []} />
    </li>
  )
}

function Batches({ filter, empty }: { filter: (b: ReminderBatchOut) => boolean; empty: string }) {
  const q = useQuery(remindersQuery())
  if (q.isPending) return <SkeletonRows count={3} />
  if (q.isError) {
    return <ErrorBanner title="Couldn't load the reminders" detail={describeError(q.error)} onRetry={() => void q.refetch()} />
  }
  const rows = q.data.filter(filter)
  if (!rows.length) return <p className="py-3 text-sm text-muted-foreground">{empty}</p>
  return (
    <ul className="space-y-2">
      {rows.map((b) => (
        <BatchCard key={b.event_id} b={b} />
      ))}
    </ul>
  )
}

function NextRun() {
  const q = useQuery(previewQuery())
  if (q.isPending) return <SkeletonRows count={3} />
  if (q.isError) {
    return <ErrorBanner title="Couldn't compute the next run" detail={describeError(q.error)} onRetry={() => void q.refetch()} />
  }
  const p = q.data
  const skipped = new Map<string, number>()
  for (const s of p.skipped) skipped.set(s.reason, (skipped.get(s.reason) ?? 0) + 1)
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        What a run in auto would send now (computed only; nothing is written).
        {p.quiet_hours ? ' Quiet hours: nothing goes out until 08:00 on a weekday.' : ''}
      </p>
      {p.batches.length ? (
        <ul className="space-y-2">
          {p.batches.map((b) => (
            <li key={b.recipient} className="rounded-lg border border-border bg-card p-3">
              <div className="flex items-center gap-1.5">
                <Chip tone="outline">{KIND_LABEL[recipientKind(b.recipient)]}</Chip>
                <span className="font-medium">{b.recipient_display ?? 'Unknown recipient'}</span>
                <span className="ml-auto text-xs text-subtle">
                  {b.items.length} item{b.items.length === 1 ? '' : 's'}
                </span>
              </div>
              <Items items={b.items} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Nothing is due for a reminder right now.</p>
      )}
      {skipped.size ? (
        <div className="flex flex-wrap gap-1">
          {[...skipped].map(([reason, n]) => (
            <Chip key={reason} tone="neutral">
              {n} skipped: {reason.replaceAll('_', ' ')}
            </Chip>
          ))}
        </div>
      ) : null}
    </div>
  )
}

const MODES: { value: ReminderMode; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: 'shadow', label: 'Shadow' },
  { value: 'auto', label: 'Auto' },
]

function ModePicker({
  label,
  detail,
  value,
  disabled,
  autoWarning,
  onChange,
}: {
  label: string
  detail: string
  value: ReminderMode
  disabled: boolean
  autoWarning: string
  onChange: (mode: ReminderMode) => void
}) {
  const id = React.useId()
  const disabledReasonId = React.useId()
  const disabledReason = disabled ? 'Only a manager can change this, or settings are being saved.' : undefined
  return (
    <fieldset className="space-y-1.5" aria-labelledby={id}>
      <div>
        <span id={id} className="font-medium">
          {label}
        </span>
        <p className="text-xs text-muted-foreground">{detail}</p>
      </div>
      <div className="flex gap-1" role="radiogroup" aria-label={label}>
        {MODES.map((m) =>
          m.value === 'auto' && m.value !== value ? (
            <ConfirmButton
              key={m.value}
              size="sm"
              role="radio"
              aria-checked={false}
              variant="outline"
              disabled={disabled}
              disabledReason={disabledReason}
              disabledReasonId={disabledReasonId}
              confirmLabel="Turn on auto?"
              onConfirm={() => onChange('auto')}
            >
              {m.label}
            </ConfirmButton>
          ) : (
            <Button
              key={m.value}
              size="sm"
              role="radio"
              aria-checked={value === m.value}
              variant={value === m.value ? 'primary' : 'outline'}
              disabled={disabled}
              disabledReason={disabledReason}
              disabledReasonId={disabledReasonId}
              onClick={() => {
                if (m.value !== value) onChange(m.value)
              }}
            >
              {m.label}
            </Button>
          ),
        )}
      </div>
      {disabledReason ? (
        <p id={disabledReasonId} className="text-xs text-muted-foreground">
          {disabledReason}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">Before Auto: {autoWarning}</p>
    </fieldset>
  )
}

/** "2, 5, 9" -> [2, 5, 9] when it is 1-3 increasing whole business days of 1-30; otherwise null. */
export function parseCadence(text: string): number[] | null {
  const parts = text
    .split(/[\s,]+/)
    .map((p) => p.trim())
    .filter(Boolean)
  if (parts.length < 1 || parts.length > 3 || parts.some((p) => !/^\d+$/.test(p))) return null
  const days = parts.map(Number)
  if (days.some((d) => d < 1 || d > 30) || days.some((d, i) => i > 0 && d <= (days[i - 1] ?? 0))) return null
  return days
}

function FollowupCadence({
  days,
  closeAfter,
  disabled,
  disabledReason,
  onSave,
}: {
  days: number[]
  closeAfter: number
  disabled: boolean
  disabledReason?: string
  onSave: (days: number[], closeAfter: number) => void
}) {
  const [text, setText] = React.useState(days.join(', '))
  const [close, setClose] = React.useState(String(closeAfter))
  React.useEffect(() => setText(days.join(', ')), [days])
  React.useEffect(() => setClose(String(closeAfter)), [closeAfter])
  const parsed = parseCadence(text)
  const closeN = /^\d+$/.test(close) ? Number(close) : Number.NaN
  const closeOk = closeN >= 1 && closeN <= 30
  const changed = parsed !== null && (parsed.join(',') !== days.join(',') || closeN !== closeAfter)
  // #1228: the Save button says why it can't be clicked, in the order a person would fix it.
  const saveBlocked = disabled
    ? (disabledReason ?? 'Not available right now')
    : parsed === null
      ? 'Fix the follow-up days first'
      : !closeOk
        ? 'Pick a close-after of 1 to 30 business days first'
        : !changed
          ? 'Change something first'
          : undefined
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1">
          <span className="block text-sm font-medium">Follow-up days (business days, at most 3)</span>
          <input
            className="block h-8 w-36 rounded-md border border-border-strong bg-card px-2 text-sm"
            value={text}
            disabled={disabled}
            aria-invalid={parsed === null}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <label className="space-y-1">
          <span className="block text-sm font-medium">Propose the close after (business days)</span>
          <input
            className="block h-8 w-20 rounded-md border border-border-strong bg-card px-2 text-sm"
            value={close}
            disabled={disabled}
            aria-invalid={!closeOk}
            inputMode="numeric"
            onChange={(e) => setClose(e.target.value)}
          />
        </label>
        <Button
          size="sm"
          variant="outline"
          disabled={saveBlocked !== undefined}
          disabledReason={saveBlocked}
          onClick={() => parsed && onSave(parsed, closeN)}
        >
          Save cadence
        </Button>
      </div>
      {parsed === null ? (
        <p className="text-xs text-destructive">Use 1 to 3 increasing whole numbers from 1 to 30, like 2, 5, 9.</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Counted from our last reply, or from when Patchbay first saw the ticket waiting if that was later. Business hours only.
        </p>
      )}
    </div>
  )
}

function StringListSetting({
  label,
  detail,
  values,
  allowEmpty,
  disabled,
  onSave,
}: {
  label: string
  detail: string
  values: string[]
  allowEmpty?: boolean
  disabled: boolean
  onSave: (values: string[]) => void
}) {
  const [text, setText] = React.useState(values.join('\n'))
  React.useEffect(() => setText(values.join('\n')), [values])
  const parsed = [
    ...new Set(
      text
        .split(/[\n,]+/)
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  ]
  const valid = allowEmpty || parsed.length > 0
  const changed = parsed.join('\n') !== values.join('\n')
  return (
    <div className="space-y-1.5">
      <label>
        <span className="font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground">{detail}</span>
        <textarea
          className="mt-1 min-h-24 w-full rounded-md border border-border-strong bg-card px-2 py-1.5 text-sm"
          value={text}
          disabled={disabled}
          aria-invalid={!valid}
          onChange={(event) => setText(event.target.value)}
        />
      </label>
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || !valid || !changed}
        disabledReason={
          disabled
            ? 'Only a manager can change this, or settings are being saved.'
            : !valid
              ? 'Add at least one value'
              : 'Change the list first'
        }
        onClick={() => onSave(parsed)}
      >
        Save list
      </Button>
    </div>
  )
}

function PartsCheckinDays({ days, disabled, onSave }: { days: number; disabled: boolean; onSave: (days: number) => void }) {
  const [text, setText] = React.useState(String(days))
  React.useEffect(() => setText(String(days)), [days])
  const n = /^\d+$/.test(text) ? Number(text) : Number.NaN
  const ok = n >= 1 && n <= 90
  const changed = ok && n !== days
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1">
          <span className="block text-sm font-medium">Parts check-in (days)</span>
          <input
            type="number"
            className="block h-8 w-20 rounded-md border border-border-strong bg-card px-2 text-sm"
            value={text}
            disabled={disabled}
            aria-label="Parts check-in interval in days"
            aria-invalid={!ok}
            inputMode="numeric"
            min={1}
            max={90}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <Button
          size="sm"
          variant="outline"
          disabled={disabled || !changed}
          disabledReason={
            disabled
              ? 'Only a manager can change this, or settings are being saved.'
              : !ok
                ? 'Use a whole number from 1 to 90'
                : 'Change the interval first'
          }
          onClick={() => onSave(n)}
        >
          Save interval
        </Button>
      </div>
      {ok ? (
        <p className="text-xs text-muted-foreground">
          While parts are on order and nothing else is due, this joins the usual reminder every {n} days. The default is 7.
        </p>
      ) : (
        <p className="text-xs text-destructive">Use a whole number from 1 to 90.</p>
      )}
    </div>
  )
}

const ESCALATION: { value: CloseoutEscalation['mode']; label: string }[] = [
  { value: 'users', label: 'Clayton only (the default)' },
  { value: 'owner_manager', label: "The case owner's manager" },
  { value: 'all_managers', label: 'Every manager' },
]

/**
 * Every automatic sender's mode in one place (#233): customer follow-ups, close-out reminders, vendor chase and the
 * immediate Desk time nudges. Shown in the Follow-ups screen's Settings tab and in Admin → Settings → Automation.
 */
export function AutomationSettings({ manager }: { manager: boolean }) {
  const q = useQuery(closeoutSettingsQuery())
  const team = useQuery(teamNotificationsQuery())
  const save = useSaveCloseoutSettings()
  const saveTeam = useSaveTeamNotifications()
  if (q.isPending) return <SkeletonRows count={3} />
  if (q.isError) {
    return <ErrorBanner title="Couldn't load the settings" detail={describeError(q.error)} onRetry={() => void q.refetch()} />
  }
  const s = q.data
  const busy = !manager || save.isPending
  const put = (body: Parameters<typeof save.mutate>[0], done: string) =>
    save.mutate(body, {
      onSuccess: () => toast.success(done),
      onError: (e) => toast.error(`Not saved: ${describeError(e)}`),
    })
  const escalation = s.closeout_escalation ?? { mode: 'users', users: [] }
  return (
    <div className="space-y-4">
      {!manager ? <p className="text-sm text-muted-foreground">Only a manager can change these.</p> : null}
      <ModePicker
        label="Riley safe-category answers"
        detail="Shadow runs the blocking guard and records what Riley would send. Auto sends only allow-listed, safe-auto replies during business hours."
        value={s.auto_answer ?? 'shadow'}
        disabled={busy}
        autoWarning="Auto replies to customers as Riley only when Riley's safety gates and Patchbay's category and account lists all allow it. The exact final bytes must pass the blocking guard."
        onChange={(m) => put({ auto_answer: m }, `Riley answers: ${m}`)}
      />
      <StringListSetting
        label="Allowed Riley categories"
        detail="One per line. Exact Riley labels match; the built-in KB how-to, password reset, status check, and front-desk families use their grounded safe-category rules."
        values={s.auto_answer_categories ?? []}
        disabled={busy}
        onSave={(values) => put({ auto_answer_categories: values }, 'Auto-answer categories saved')}
      />
      <StringListSetting
        label="Never-auto customers"
        detail="Account ids, account names, customer email addresses, or email domains, one per line. Put VIP accounts here."
        values={s.never_auto_accounts ?? []}
        allowEmpty
        disabled={busy}
        onSave={(values) => put({ never_auto_accounts: values }, 'Never-auto customers saved')}
      />
      <ModePicker
        label="Customer follow-ups (waiting on the customer)"
        detail="Replaces Desk's waiting-on-customer emails. Scheduled from our last reply, never at it; stops when the customer replies; proposes the close after the last one."
        value={s.customer_followups ?? 'shadow'}
        disabled={busy}
        autoWarning="Auto emails customers on their tickets as Riley, after the guard approves the exact text, with you recorded as the approver, and posts a private Desk note for every follow-up and skip. Review the shadow drafts first, then switch off Desk's own waiting-on-customer alerts."
        onChange={(m) => put({ customer_followups: m }, `Customer follow-ups: ${m}`)}
      />
      <FollowupCadence
        days={s.customer_followup_days ?? [2, 5, 9]}
        closeAfter={s.customer_followup_close_after_days ?? 2}
        disabled={busy}
        disabledReason={!manager ? 'Only a manager can change this' : 'Saving…'}
        onSave={(days, closeAfter) =>
          put({ customer_followup_days: days, customer_followup_close_after_days: closeAfter }, 'Follow-up cadence saved')
        }
      />
      <ModePicker
        label="Open-case hygiene"
        detail="Internal morning digest, My Day pin, manager stale alerts and private Riley nudge notes. The code default is live."
        value={s.open_case_hygiene?.mode ?? 'auto'}
        disabled={busy}
        autoWarning="Auto notifies staff only. A private Desk note starts after three business days; this family never emails a customer."
        onChange={(m) => put({ open_case_hygiene: { ...s.open_case_hygiene, mode: m } }, `Open-case hygiene: ${m}`)}
      />
      <ModePicker
        label="Close-out reminders"
        detail="Contractors, techs and billing. Shadow writes the drafts to Follow-ups and sends nothing."
        value={s.closeout_reminders}
        disabled={busy}
        autoWarning="Auto emails contractors and posts private Desk notes for techs, once a day each, outside quiet hours. Review the shadow drafts first."
        onChange={(m) => put({ closeout_reminders: m }, `Close-out reminders: ${m}`)}
      />
      <PartsCheckinDays
        days={s.parts_checkin_days ?? 7}
        disabled={busy}
        onSave={(days) => put({ parts_checkin_days: days }, 'Parts check-in interval saved')}
      />
      <ModePicker
        label="Vendor chase (overdue shipments)"
        detail="Asks the vendor for a ship date on the vendor's conversation ticket. Separate from the reminders above."
        value={s.vendor_chase ?? 'shadow'}
        disabled={busy}
        autoWarning="Auto replies to vendors on their conversation tickets after Riley's guard approves the exact text."
        onChange={(m) => put({ vendor_chase: m }, `Vendor chase: ${m}`)}
      />
      {/* biome-ignore lint/a11y/noLabelWithoutControl: Picker renders the nested combobox input. */}
      <label className="block space-y-1">
        <span className="font-medium">Escalate after 2 unanswered reminders to</span>
        <Picker
          className="block h-8 w-full rounded-md border border-border-strong bg-card px-2 text-sm"
          value={escalation.mode ?? ''}
          disabled={busy}
          onChange={(e) =>
            put({ closeout_escalation: { mode: e.target.value as CloseoutEscalation['mode'], users: [] } }, 'Escalation updated')
          }
        >
          {ESCALATION.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Picker>
        {escalation.mode === 'users' && (escalation.users?.length ?? 0) > 0 ? (
          <span className="text-xs text-muted-foreground">{escalation.users?.length} chosen people</span>
        ) : null}
      </label>
      <div className="flex items-start gap-3">
        <Switch
          aria-label="Immediate Desk nudges for replies with no time logged"
          checked={team.data?.desk_nudges ?? false}
          disabled={!manager || !team.data || saveTeam.isPending}
          onCheckedChange={(on: boolean) =>
            saveTeam.mutate(on, {
              onSuccess: () => toast.success(on ? 'Desk time nudges on' : 'Desk time nudges off'),
              onError: (e) => toast.error(`Not saved: ${describeError(e)}`),
            })
          }
        />
        <div>
          <span className="font-medium">Immediate Desk nudges (replies with no time logged)</span>
          <p className="text-xs text-muted-foreground">
            A private Riley note on the ticket, once per reply, for flags raised after it is turned on. People who turned Desk mentions off
            in their preferences are skipped. On or off: this one has no shadow mode.
          </p>
          {team.isError ? <p className="text-xs text-destructive">Couldn't read this switch.</p> : null}
        </div>
      </div>
      <p className="text-sm">
        <span className="font-medium">Quiet hours:</span>{' '}
        <span className="text-muted-foreground">{s.quiet_hours ?? '18:00-08:00 and weekends, America/Toronto'}</span>
      </p>
    </div>
  )
}

export const REMINDER_TABS = ['answers', 'stale', 'drafts', 'outcomes', 'next', 'settings'] as const
export type ReminderTab = (typeof REMINDER_TABS)[number]

/**
 * The follow-ups and reminders review (#121, #216, #233). Uncontrolled by default (Home's dialog); the Follow-ups
 * screen passes `tab`/`onTab` so the open tab lives in the URL and the case trail can link straight to it.
 */
export function RemindersPanel({
  manager,
  initialTab = 'drafts',
  tab,
  onTab,
}: {
  manager: boolean
  initialTab?: ReminderTab
  tab?: ReminderTab
  onTab?: (tab: ReminderTab) => void
}) {
  const settings = useQuery(closeoutSettingsQuery())
  const controlled =
    tab !== undefined ? { value: tab, onValueChange: (v: unknown) => onTab?.(v as ReminderTab) } : { defaultValue: initialTab }
  return (
    <div className="space-y-3" data-testid="reminders-panel">
      {settings.data ? (
        <div className="flex flex-wrap gap-1.5 text-sm">
          <Chip tone={settings.data.customer_followups === 'auto' ? 'success' : 'primary'}>
            Customer follow-ups: {settings.data.customer_followups ?? 'shadow'}
          </Chip>
          <Chip tone={settings.data.closeout_reminders === 'auto' ? 'success' : 'primary'}>
            Reminders: {settings.data.closeout_reminders}
          </Chip>
          <Chip tone={settings.data.open_case_hygiene?.mode === 'auto' ? 'success' : 'primary'}>
            Hygiene: {settings.data.open_case_hygiene?.mode ?? 'auto'}
          </Chip>
          <Chip tone={settings.data.vendor_chase === 'auto' ? 'success' : 'primary'}>
            Vendor chase: {settings.data.vendor_chase ?? 'shadow'}
          </Chip>
          <Chip tone={settings.data.auto_answer === 'auto' ? 'success' : 'primary'}>
            Riley answers: {settings.data.auto_answer ?? 'shadow'}
          </Chip>
        </div>
      ) : null}
      <Tabs {...controlled}>
        <TabsList>
          <TabsTrigger value="answers">Riley would answer</TabsTrigger>
          <TabsTrigger value="stale">Stale: closing soon</TabsTrigger>
          <TabsTrigger value="drafts">Shadow drafts</TabsTrigger>
          <TabsTrigger value="outcomes">Sent and skipped</TabsTrigger>
          <TabsTrigger value="next">Next run</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="answers" className="pt-3">
          <AutoAnswerPanel />
        </TabsContent>
        <TabsContent value="stale" className="pt-3">
          <StaleClosePanel manager={manager} />
        </TabsContent>
        <TabsContent value="drafts" className="pt-3">
          <Batches filter={(b) => b.status === 'drafted'} empty="No shadow drafts yet. They appear after the next run on a weekday." />
        </TabsContent>
        <TabsContent value="outcomes" className="pt-3">
          <Batches filter={(b) => b.status !== 'drafted'} empty="Nothing has been sent, skipped, blocked or failed." />
        </TabsContent>
        <TabsContent value="next" className="pt-3">
          <NextRun />
        </TabsContent>
        <TabsContent value="settings" className="pt-3">
          <AutomationSettings manager={manager} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
